import {
  AfterViewInit,
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
  inject,
  ViewContainerRef,
  ComponentRef,
  ViewEncapsulation,
  Type,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { Crepe } from '@milkdown/crepe';
import { listener, listenerCtx } from '@milkdown/plugin-listener';
import { commandsCtx, editorViewCtx } from '@milkdown/kit/core';
import { callCommand } from '@milkdown/kit/utils';
import { replaceAll } from '@milkdown/utils';
import { deleteSelection } from '@milkdown/kit/prose/commands';
import { NodeSelection, TextSelection } from '@milkdown/kit/prose/state';
import {
  turnIntoTextCommand,
  wrapInHeadingCommand,
  wrapInBulletListCommand,
  wrapInOrderedListCommand,
  wrapInBlockquoteCommand,
  createCodeBlockCommand,
  clearTextInCurrentBlockCommand,
} from '@milkdown/preset-commonmark';

import {
  NzContextMenuService,
  NzDropdownMenuComponent,
  NzDropDownModule,
} from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzButtonModule } from 'ng-zorro-antd/button';

import {
  PublicFileUploadService,
  PrivateFileUploadService,
} from '@rapider/services';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { DynamicFormComponent, DynamicFormSchema } from '@rapider/dynamic-form';

export interface WidgetManifest {
  title: string;
  componentClassName: string;
  inputsSchema: DynamicFormSchema;
  loader?: () => Promise<Type<any>>;
}

// Temporary registry for testing
const WIDGET_REGISTRY: Record<string, WidgetManifest> = {
  'rapider-alert': {
    title: 'Alert Box',
    componentClassName: 'rapider-alert',
    loader: () =>
      import('./components/alert/alert.component').then(
        (m) => m.AlertComponent,
      ),
    inputsSchema: {
      fields: [
        { name: 'title', type: 'string', label: 'Title' },
        {
          name: 'description',
          type: 'string',
          label: 'Description',
          displayWithComponent: 'Textarea',
        },
        { name: 'icon', type: 'string', label: 'Icon (FontAwesome)' },
        {
          name: 'type',
          type: 'string',
          label: 'Alert Type',
          displayWithComponent: 'Select',
          options: [
            { label: 'Success', value: 'success' },
            { label: 'Info', value: 'info' },
            { label: 'Warning', value: 'warning' },
            { label: 'Error', value: 'error' },
            { label: 'Default', value: 'default' },
            { label: 'Light Gray', value: 'light-gray' },
          ],
        },
      ],
    },
  },
};

/**
 * @class RapiderMarkdownEditorComponent
 * @description Standalone wrapper around Milkdown's Crepe editor context.
 */
@Component({
  selector: 'rapider-markdown-editor',
  standalone: true,
  imports: [
    CommonModule,
    NzDropDownModule,
    NzMenuModule,
    NzDrawerModule,
    NzButtonModule,
    DynamicFormComponent,
  ],
  host: { class: 'w-full h-full flex-1 min-h-0 flex flex-col' },
  encapsulation: ViewEncapsulation.None,
  template: `
    <div
      class="rapider-markdown-wrapper w-full h-full flex-1 min-h-0 bg-component-inner overflow-y-auto custom-scrollbar relative flex flex-col"
    >
      <!-- Quick Insert Toolbar was removed and moved to Slash Menu -->

      <div #editorRoot class="markdown-editor min-h-full flex-1 w-full flex flex-col"></div>
    </div>

    <!-- Custom Notion-Style Block Context Menu -->
    <nz-dropdown-menu
      #blockMenu="nzDropdownMenu"
      nzOverlayClassName="crepe-dropdown-menu"
    >
      <ul nz-menu class="min-w-[220px]">
        <!-- Edit Widget (Dynamic) -->
        @if (selectedWidgetManifest) {
          <li
            nz-menu-item
            (click)="editSelectedWidget()"
            style="color: var(--color-primary); font-weight: 600;"
          >
            <i class="fa-regular fa-pen-to-square mr-2 w-4"></i> Edit
            {{ selectedWidgetManifest.title }}
          </li>
          <li nz-menu-divider></li>
        }

        @if (!selectedWidgetManifest) {
          <!-- Turn Into Submenu -->
          <li nz-submenu nzTitle="Turn into" nzIcon="swap">
            <ul>
              <li nz-menu-item (click)="turnInto('text')">
                <i class="fa-regular fa-text mr-2 w-4"></i> Text
              </li>
              <li nz-menu-item (click)="turnInto('h1')">
                <i class="fa-regular fa-h1 mr-2 w-4"></i> Heading 1
              </li>
              <li nz-menu-item (click)="turnInto('h2')">
                <i class="fa-regular fa-h2 mr-2 w-4"></i> Heading 2
              </li>
              <li nz-menu-item (click)="turnInto('h3')">
                <i class="fa-regular fa-h3 mr-2 w-4"></i> Heading 3
              </li>
              <li nz-menu-item (click)="turnInto('bullet_list')">
                <i class="fa-regular fa-list-ul mr-2 w-4"></i> Bulleted list
              </li>
              <li nz-menu-item (click)="turnInto('ordered_list')">
                <i class="fa-regular fa-list-ol mr-2 w-4"></i> Numbered list
              </li>
              <li nz-menu-item (click)="turnInto('blockquote')">
                <i class="fa-regular fa-quote-right mr-2 w-4"></i> Quote
              </li>
              <li nz-menu-item (click)="turnInto('code_block')">
                <i class="fa-regular fa-code mr-2 w-4"></i> Code
              </li>
            </ul>
          </li>

          <li nz-menu-divider></li>
        }

        <!-- Copy & Duplicate -->
        <li
          nz-menu-item
          (click)="copySelectedBlock()"
          class="flex items-center gap-2"
        >
          <i class="fa-regular fa-copy w-4"></i>
          <span i18n="@@copy">Copy</span>
        </li>
        <li
          nz-menu-item
          (click)="duplicateSelectedBlock()"
          class="flex items-center gap-2"
        >
          <i class="fa-regular fa-clone w-4"></i>
          <span i18n="@@duplicate">Duplicate</span>
        </li>

        <li nz-menu-divider></li>

        <!-- Delete -->
        <li
          nz-menu-item
          (click)="deleteSelectedBlock()"
          class="text-danger flex items-center gap-2"
        >
          <i class="fa-regular fa-trash w-4"></i>
          <span i18n="@@delete">Delete</span>
        </li>
      </ul>
    </nz-dropdown-menu>

    <!-- Drawer for dynamic form -->
    <nz-drawer
      [nzVisible]="isWidgetModalVisible"
      [nzTitle]="
        (isCreatingNewWidget ? 'Create ' : 'Edit ') +
        selectedWidgetManifest?.title
      "
      nzPlacement="right"
      [nzWidth]="450"
      [nzMaskStyle]="{ top: '50px' }"
      [nzBodyStyle]="{ paddingBottom: '80px' }"
      (nzOnClose)="closeWidgetModal()"
      [nzFooter]="drawerFooter"
    >
      <ng-container *nzDrawerContent>
        @if (selectedWidgetManifest) {
          <rapider-dynamic-form
            [schema]="selectedWidgetManifest.inputsSchema"
            [data]="selectedWidgetConfig?.inputs"
            (formChange)="onWidgetFormChange($event)"
          >
          </rapider-dynamic-form>
        }
      </ng-container>
    </nz-drawer>

    <!-- Footer for drawer -->
    <ng-template #drawerFooter>
      <div class="flex justify-end gap-2">
        <button nz-button nzType="default" (click)="closeWidgetModal()">
          Cancel
        </button>
        <button nz-button nzType="primary" (click)="saveWidgetForm()">
          Save
        </button>
      </div>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: flex !important;
        flex-direction: column !important;
        width: 100% !important;
        height: 100% !important;
        flex: 1 1 0% !important;
        min-height: 0 !important;
      }

      .rapider-markdown-wrapper {
        min-height: 100% !important;
        flex: 1 1 0% !important;
        display: flex !important;
        flex-direction: column !important;
      }

      .markdown-editor {
        min-height: 100% !important;
        flex: 1 1 0% !important;
        display: flex !important;
        flex-direction: column !important;
      }

      /* Scope core component space padding metrics locally */
      .milkdown {
        min-height: 100% !important;
        flex: 1 1 0% !important;
        display: flex !important;
        flex-direction: column !important;
        /* Force Crepe to use our dynamic theme variables for both Light and Dark mode */
        --crepe-font-title:
          -apple-system-body, ui-sans-serif, -apple-system, 'system-ui',
          'Segoe UI', Helvetica, 'Apple Color Emoji', Arial, 'sans-serif',
          'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji' !important;
        --crepe-font-default:
          -apple-system-body, ui-sans-serif, -apple-system, 'system-ui',
          'Segoe UI', Helvetica, 'Apple Color Emoji', Arial, 'sans-serif',
          'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji' !important;

        --crepe-color-background: transparent !important;
        --crepe-color-surface: var(--color-component-inner) !important;
        --crepe-color-surface-hover: var(--color-hover-default) !important;
        --crepe-color-surface-variant: var(--color-hover-default) !important;
        --crepe-color-on-surface: var(--color-primary-text) !important;
        --crepe-color-on-surface-variant: var(
          --color-secondary-text
        ) !important;
        --crepe-color-outline: var(--primary-text-color) !important;
        --crepe-color-primary: var(--color-primary) !important;
        --crepe-color-on-primary: var(--color-primary-button-text) !important;
        --crepe-color-error: var(--color-danger) !important;
        --crepe-color-on-error: #ffffff !important;
        --crepe-color-surface-low: var(
          --color-alt-background
        ) !important; /* default button color */
        --crepe-color-hover: var(
          --color-hover-default
        ) !important; /* default button hover color */
        --crepe-color-secondary: var(
          --color-alt-background
        ) !important; /* default button color */
        --crepe-color-selected: var(
          --color-primary
        ) !important; /* Selection color root */
      }

      .milkdown .editor {
        min-height: 100% !important;
        flex: 1 1 0% !important;
        padding: 20px 32px 80px 80px !important;
        outline: none !important;
        color: var(--color-primary-text) !important;
        font-family:
          -apple-system-body,
          ui-sans-serif,
          -apple-system,
          'system-ui',
          'Segoe UI',
          Helvetica,
          'Apple Color Emoji',
          Arial,
          'sans-serif',
          'Segoe UI Emoji',
          'Segoe UI Symbol',
          'Noto Color Emoji' !important;
        font-size: 16px !important;
        font-variation-settings: 'opsz' 16 !important;
      }

      .milkdown .editor h1,
      .milkdown .editor h2,
      .milkdown .editor h3,
      .milkdown .editor h4,
      .milkdown .editor h5,
      .milkdown .editor h6 {
        font-family: inherit !important;
        font-weight: 600 !important;
      }

      .milkdown .editor h1 {
        font-size: 24px !important;
        font-variation-settings: 'opsz' 24 !important;
      }

      .milkdown .editor h2 {
        font-size: 20px !important;
        font-variation-settings: 'opsz' 20 !important;
      }

      .milkdown .editor h3 {
        font-size: 18px !important;
        font-variation-settings: 'opsz' 18 !important;
      }

      .milkdown .editor h4,
      .milkdown .editor h5,
      .milkdown .editor h6 {
        font-size: 16px !important;
        font-variation-settings: 'opsz' 16 !important;
      }

      /* Fix block selection readability (override creepy defaults) */
      .milkdown .ProseMirror .ProseMirror-selectednode {
        background: var(--crepe-color-selected) !important;
        outline: 2px solid var(--color-primary) !important;
        border-radius: 4px;
      }

      .milkdown-block-handle {
        left: 8px !important;
        color: var(--color-secondary-text) !important;
        transition: all 0.2s ease;
      }

      .milkdown-block-handle:hover {
        color: var(--color-primary) !important;
        background-color: var(--color-hover-default) !important;
        border-radius: 4px;
      }

      .milkdown-crepe-frame {
        border: none !important;
        min-height: 100% !important;
        flex: 1 1 0% !important;
        display: flex !important;
        flex-direction: column !important;
      }

      /* --------------------------------------------------------------------------
       Element Overrides
       -------------------------------------------------------------------------- */

      /* Code Blocks (ChatGPT Style) */
      .milkdown .milkdown-code-block,
      .milkdown .milkdown-crepe-code-block {
        background-color: var(--color-component-header) !important;
        border: 1px solid var(--color-secondary-border) !important;
        border-radius: 0.5rem !important;
        /* overflow: hidden */ /*We dont want to hide overflow because of the language dropdown */
        margin: 1rem 0 !important;
      }

      /* Target the actual Shiki syntax highlighter block inside the wrapper */
      .milkdown pre.shiki {
        background-color: transparent !important; /* Let the wrapper background show through */
        padding: 1rem !important;
        margin: 0 !important;
      }

      /* Ensure text inside code block defaults to readable contrast in dark mode */
      .milkdown .milkdown-crepe-code-block code,
      .milkdown .milkdown-code-block code {
        color: var(--color-primary-text);
        font-family:
          ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
          'Liberation Mono', 'Courier New', monospace !important;
      }

      /* Math & Latex Blocks (Fixes the hardcoded red text fallback) */
      .milkdown .math-inline,
      .milkdown .math-block,
      .milkdown .katex {
        color: var(--color-primary-text) !important;
      }

      /* Image / Upload Block Placeholder */
      .milkdown-crepe-image-block {
        background-color: var(--color-alt-background) !important;
        border: 1px solid var(--color-secondary-border) !important;
        border-radius: 8px !important;
        overflow: hidden;
      }

      .milkdown-crepe-image-block .upload-placeholder,
      .milkdown-crepe-image-block input {
        color: var(--color-secondary-text) !important;
      }

      /* Placeholders */
      .milkdown .crepe-placeholder {
        color: var(--color-secondary-text) !important;
      }

      /* Obsidian-style Callouts / Blockquotes */
      .milkdown blockquote {
        border-left: 4px solid var(--color-primary) !important;
        background-color: var(--color-alt-background) !important;
        padding: 12px 18px !important;
        margin: 1rem 0 !important;
        border-radius: 0 8px 8px 0 !important;
        color: var(--color-primary-text) !important;
      }

      /* Collapsible Audit Details */
      .milkdown details {
        border: 1px solid var(--color-secondary-border) !important;
        background-color: var(--color-component-inner) !important;
        border-radius: 8px !important;
        padding: 10px 16px !important;
        margin: 1rem 0 !important;
      }

      .milkdown details summary {
        cursor: pointer !important;
        font-weight: 600 !important;
        color: var(--color-primary-text) !important;
      }

      .cm-editor,
      .cm-gutters,
      .cm-gutters-before {
        background: transparent !important;
      }

      .cm-activeLineGutter {
        background-color: #6699ff0b !important; /* #2c313a */
      }
      /* --------------------------------------------------------------------------
       NG Zorro Custom Dropdown Menu Styling (Matches Crepe)
       -------------------------------------------------------------------------- */
      .crepe-dropdown-menu .ant-dropdown-menu {
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        border: 1px solid var(--color-border-default, #e5e7eb);
        padding: 8px 0;
        background-color: var(--color-background-component, #ffffff);
      }
      .crepe-dropdown-menu .ant-dropdown-menu-item,
      .crepe-dropdown-menu .ant-dropdown-menu-submenu-title {
        padding: 8px 16px;
        font-size: 14px;
        border-radius: 6px;
        margin: 0 8px;
        width: auto;
        clear: both;
        line-height: 1.5;
      }
      .crepe-dropdown-menu .ant-dropdown-menu-item:hover,
      .crepe-dropdown-menu .ant-dropdown-menu-submenu-title:hover {
        background-color: var(--color-hover-default, #f3f4f6);
      }
      .crepe-dropdown-menu
        .ant-dropdown-menu-submenu-title
        .ant-dropdown-menu-submenu-arrow {
        right: 8px;
      }
    `,
  ],
})
export class RapiderMarkdownEditorComponent
  implements AfterViewInit, OnDestroy, AfterViewChecked
{
  @ViewChild('editorRoot', { static: true })
  editorRoot!: ElementRef<HTMLDivElement>;

  @ViewChild('blockMenu') blockMenu!: NzDropdownMenuComponent;

  private _value = '';
  private isMounted = false;

  /** Input: The initial Markdown content */
  @Input()
  get value(): string {
    return this._value;
  }
  set value(val: string) {
    const newVal = val == null ? '' : String(val);
    if (this._value !== newVal) {
      this._value = newVal;
      if (this.crepe && this.isMounted) {
        try {
          this.crepe.editor.action(replaceAll(newVal) as any);
        } catch (e) {
          console.warn('[RapiderMarkdownEditor] replaceAll failed:', e);
        }
      }
    }
  }

  public setMarkdown(val: string): void {
    this.value = val;
  }

  public getMarkdown(): string {
    return this.value;
  }

  /** Input: The placeholder text for empty states */
  @Input() placeholder = 'Write documentation...';

  /** Input: Whether uploaded files should be public (CDN) or private (Presigned URLs) */
  @Input() isPublic = false;

  /** Input: The semantic entity type for the file upload path (e.g., 'app', 'space', 'document') */
  @Input() fileType = 'document';

  /** Input: The optional parent entity ID to associate the uploaded files with */
  @Input() parentEntityId?: string;

  /** Output: Emits changes whenever the content is modified by the user */
  @Output() valueChange = new EventEmitter<string>();

  private crepe?: Crepe;

  private readonly publicFileUploadService = inject(PublicFileUploadService);
  private readonly privateFileUploadService = inject(PrivateFileUploadService);
  private readonly nzContextMenuService = inject(NzContextMenuService);
  private readonly vcr = inject(ViewContainerRef);

  private resolvedImages = new Map<string, string>();
  private resolvingImages = new Set<string>();

  private renderedWidgets = new Map<Element, ComponentRef<any>>();
  private renderPollingInterval: any;

  // Widget Edit State
  selectedWidgetNode?: any;
  selectedWidgetPos?: number;
  selectedWidgetConfig?: any;
  selectedWidgetManifest?: WidgetManifest;
  isWidgetModalVisible = false;
  private pendingWidgetFormData: any;
  protected isCreatingNewWidget = false;

  constructor(private zone: NgZone) {
    this.ensureMaterialSymbolsLoaded();
  }

  public get widgetRegistryList(): WidgetManifest[] {
    return Object.values(WIDGET_REGISTRY);
  }

  insertWidget(manifest: WidgetManifest) {
    if (!this.crepe) return;

    this.selectedWidgetManifest = manifest;
    this.selectedWidgetConfig = {
      componentClassName: manifest.componentClassName,
      inputs: {},
    };
    this.pendingWidgetFormData = {};
    this.isCreatingNewWidget = true;
    this.isWidgetModalVisible = true;
  }

  /**
   * Dynamically injects the Google Font for Material Symbols directly into the document `<head>`.
   * This guarantees the icons load even if Angular strips the `@import` from the encapsulated stylesheet.
   */
  private ensureMaterialSymbolsLoaded(): void {
    if (typeof document !== 'undefined') {
      const id = 'material-symbols-font';
      if (!document.getElementById(id)) {
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href =
          'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200';
        document.head.appendChild(link);
      }
    }
  }

  ngAfterViewInit(): void {
    // Run outside Angular's zone to prevent excessive change detection cycles while typing
    this.zone.runOutsideAngular(async () => {
      this.crepe = new Crepe({
        root: this.editorRoot.nativeElement,
        defaultValue: this._value || '',
        features: {
          [Crepe.Feature.ImageBlock]: true,
          [Crepe.Feature.BlockEdit]: true,
        },
        featureConfigs: {
          [Crepe.Feature.BlockEdit]: {
            buildMenu: (builder: any) => {
              const advancedGroup = builder.getGroup('advanced');
              Object.values(WIDGET_REGISTRY).forEach((manifest) => {
                advancedGroup.addItem('widget_' + manifest.componentClassName, {
                  label: manifest.title + ' Widget',
                  icon: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 16.875h3.375m0 0h3.375m-3.375 0V13.5m0 3.375v3.375M6 10.5h2.25a2.25 2.25 0 0 0 2.25-2.25V6a2.25 2.25 0 0 0-2.25-2.25H6A2.25 2.25 0 0 0 3.75 6v2.25A2.25 2.25 0 0 0 6 10.5Zm0 9.75h2.25A2.25 2.25 0 0 0 10.5 18v-2.25a2.25 2.25 0 0 0-2.25-2.25H6a2.25 2.25 0 0 0-2.25 2.25V18A2.25 2.25 0 0 0 6 20.25Zm9.75-9.75H18a2.25 2.25 0 0 0 2.25-2.25V6A2.25 2.25 0 0 0 18 3.75h-2.25A2.25 2.25 0 0 0 13.5 6v2.25a2.25 2.25 0 0 0 2.25 2.25Z" /></svg>`,
                  onRun: (ctx: any) => {
                    this.zone.run(() => {
                      const commands = ctx.get(commandsCtx);
                      commands.call(clearTextInCurrentBlockCommand.key);
                      this.insertWidget(manifest);
                    });
                  },
                });
              });
            },
          },
          [Crepe.Feature.ImageBlock]: {
            onUpload: async (file: File) => {
              try {
                if (this.isPublic) {
                  // Upload directly to the CDN via the robust PublicFileUploadService
                  return await this.publicFileUploadService.uploadPublicFile(
                    file,
                    this.fileType,
                    this.parentEntityId,
                  );
                } else {
                  // Upload privately and return a relative path custom protocol identifier.
                  // Using a relative path perfectly avoids DOMPurify stripping the src attribute!
                  const projectFile =
                    await this.privateFileUploadService.uploadPrivateFile(
                      file,
                      this.fileType,
                      this.parentEntityId,
                    );
                  return `/rapider-private-file/${projectFile.id}`;
                }
              } catch (error) {
                console.error('[Milkdown] Failed to upload image:', error);
                // Fallback to local object URL so the editor doesn't completely break
                return URL.createObjectURL(file);
              }
            },
          },
        },
      });

      // Attach the listener plugin to capture markdown updates and emit them safely back into Angular
      this.crepe.editor.use(listener).config((ctx: any) => {
        ctx
          .get(listenerCtx)
          .mounted(() => {
            this.isMounted = true;
            if (this._value) {
              try {
                this.crepe?.editor.action(replaceAll(this._value) as any);
              } catch (_) {}
            }
            this.zone.run(() => {
              setTimeout(() => this.renderDynamicWidgets(), 100);
            });
          })
          .markdownUpdated(
            (ctx: any, markdown: string, prevMarkdown: string) => {
              this.zone.run(() => {
                this._value = markdown;
                this.valueChange.emit(markdown);
                setTimeout(() => this.renderDynamicWidgets(), 50);
              });
            },
          );
      });

      await this.crepe.create();

      // Robust fallback polling to catch async CodeMirror initializations
      this.zone.runOutsideAngular(() => {
        this.renderPollingInterval = setInterval(() => {
          this.zone.run(() => this.renderDynamicWidgets());
        }, 500);
      });
    });

    document.addEventListener('click', this.handleBlockMenuClick);
  }

  private handleBlockMenuClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const blockHandleContainer = target.closest('.milkdown-block-handle');

    if (blockHandleContainer) {
      // The container has two `.operation-item` children:
      // [0] = Plus Button, [1] = Drag Handle (6-dots)
      const operationItems =
        blockHandleContainer.querySelectorAll('.operation-item');
      const clickedOperationItem = target.closest('.operation-item');

      // Only open the menu if the user explicitly clicked the drag handle
      if (
        operationItems.length >= 2 &&
        clickedOperationItem === operationItems[1]
      ) {
        event.preventDefault();
        event.stopPropagation();

        // Reset state
        this.selectedWidgetNode = undefined;
        this.selectedWidgetPos = undefined;
        this.selectedWidgetConfig = undefined;
        this.selectedWidgetManifest = undefined;
        this.isCreatingNewWidget = false;

        this.crepe?.editor?.action((ctx) => {
          const view = ctx.get(editorViewCtx);
          const selection = view.state.selection;

          if (
            selection instanceof NodeSelection &&
            selection.node.type.name === 'code_block'
          ) {
            const lang = selection.node.attrs['language'];
            if (lang === 'widget') {
              this.selectedWidgetNode = selection.node;
              this.selectedWidgetPos = selection.from;
              try {
                this.selectedWidgetConfig = JSON.parse(
                  selection.node.textContent,
                );
                if (this.selectedWidgetConfig?.componentClassName) {
                  this.selectedWidgetManifest =
                    WIDGET_REGISTRY[
                      this.selectedWidgetConfig.componentClassName
                    ];
                }
              } catch (e) {
                console.error('Failed to parse widget config', e);
              }
            }
          }
        });

        // Trigger change detection since we're outside angular zone usually
        this.zone.run(() => {
          this.nzContextMenuService.create(event, this.blockMenu);
        });
      }
    }
  };

  public editSelectedWidget() {
    this.isWidgetModalVisible = true;
  }

  public closeWidgetModal() {
    this.isWidgetModalVisible = false;
    this.pendingWidgetFormData = null;
    this.isCreatingNewWidget = false;
  }

  public onWidgetFormChange(data: any) {
    this.pendingWidgetFormData = data;
  }

  public saveWidgetForm() {
    if (!this.crepe?.editor || !this.selectedWidgetConfig) {
      this.closeWidgetModal();
      return;
    }

    // Merge new inputs
    const newConfig = {
      ...this.selectedWidgetConfig,
      inputs: {
        ...this.selectedWidgetConfig.inputs,
        ...this.pendingWidgetFormData,
      },
    };

    const textContent = JSON.stringify(newConfig, null, 2);

    this.crepe.editor.action((ctx) => {
      const view = ctx.get(editorViewCtx);
      const { state, dispatch } = view;
      const { tr, schema } = state;

      if (this.isCreatingNewWidget) {
        const codeBlockType = schema.nodes['code_block'];
        if (codeBlockType) {
          const textNode = schema.text(textContent);
          const codeBlockNode = codeBlockType.create(
            { language: 'widget' },
            textNode,
          );

          const newTr = tr.replaceSelectionWith(codeBlockNode);
          dispatch(newTr);
          view.focus();
        }
      } else if (this.selectedWidgetPos !== undefined) {
        const pos = this.selectedWidgetPos;
        const node = view.state.doc.nodeAt(pos);

        if (node && node.type.name === 'code_block') {
          tr.replaceWith(
            pos + 1,
            pos + 1 + node.content.size,
            schema.text(textContent),
          );
          dispatch(tr);
        }
      }
    });

    this.closeWidgetModal();
  }

  public turnInto(
    type:
      | 'text'
      | 'h1'
      | 'h2'
      | 'h3'
      | 'bullet_list'
      | 'ordered_list'
      | 'blockquote'
      | 'code_block',
  ) {
    if (!this.crepe?.editor) return;

    this.crepe.editor.action((ctx) => {
      const view = ctx.get(editorViewCtx);
      const { state, dispatch } = view;
      const { selection, tr, schema } = state;

      // Special handling for converting complex blocks (code_block, blockquote, lists)
      // ProseMirror's default commands struggle to unwrap these cleanly
      if (selection instanceof NodeSelection) {
        const node = selection.node;
        const isComplexBlock = [
          'code_block',
          'blockquote',
          'bullet_list',
          'ordered_list',
          'list_item',
        ].includes(node.type.name);
        const isTargetSimpleBlock = ['text', 'h1', 'h2', 'h3'].includes(type);

        if (isComplexBlock && isTargetSimpleBlock) {
          if (node.type.name === 'code_block') {
            const pType = schema.nodes['paragraph'];
            if (pType) {
              const newPara = pType.create(
                null,
                node.textContent ? schema.text(node.textContent) : null,
              );
              tr.replaceWith(selection.from, selection.to, newPara);
              // Set selection inside the new paragraph so subsequent commands target it
              tr.setSelection(TextSelection.create(tr.doc, selection.from + 1));
              dispatch(tr);
            }
          } else {
            // Unwrap blockquote or list by replacing the node with its inner content
            tr.replaceWith(selection.from, selection.to, node.content);
            // Set selection inside the newly unwrapped content
            tr.setSelection(TextSelection.create(tr.doc, selection.from + 1));
            dispatch(tr);
          }
          // Note: we do NOT return here, we let the switch statement below execute
          // so it can apply the desired heading formatting (if any) to the unwrapped text!
        }
      }

      switch (type) {
        case 'text':
          ctx.get(commandsCtx).call(turnIntoTextCommand.key as any);
          break;
        case 'h1':
          ctx.get(commandsCtx).call(wrapInHeadingCommand.key as any, 1);
          break;
        case 'h2':
          ctx.get(commandsCtx).call(wrapInHeadingCommand.key as any, 2);
          break;
        case 'h3':
          ctx.get(commandsCtx).call(wrapInHeadingCommand.key as any, 3);
          break;
        case 'bullet_list':
          ctx.get(commandsCtx).call(wrapInBulletListCommand.key as any);
          break;
        case 'ordered_list':
          ctx.get(commandsCtx).call(wrapInOrderedListCommand.key as any);
          break;
        case 'blockquote':
          ctx.get(commandsCtx).call(wrapInBlockquoteCommand.key as any);
          break;
        case 'code_block':
          ctx.get(commandsCtx).call(createCodeBlockCommand.key as any);
          break;
      }
    });
  }

  public copySelectedBlock() {
    if (this.crepe?.editor) {
      document.execCommand('copy');
    }
  }

  public duplicateSelectedBlock() {
    if (!this.crepe?.editor) return;

    this.crepe.editor.action((ctx) => {
      const view = ctx.get(editorViewCtx);
      const { state, dispatch } = view;
      const { tr, selection } = state;

      if (selection instanceof NodeSelection) {
        const node = selection.node;
        const insertPos = selection.to;
        const newTr = tr.insert(
          insertPos,
          node.type.create(node.attrs, node.content, node.marks),
        );
        dispatch(newTr);
      }
    });

    // CodeMirror takes a moment to initialize the new block's DOM.
    // Try rendering a few times to be sure we catch it once it has text
    let attempts = 0;
    const interval = setInterval(() => {
      this.renderDynamicWidgets();
      attempts++;
      if (attempts >= 10) clearInterval(interval);
    }, 100);
  }

  public deleteSelectedBlock() {
    if (this.crepe?.editor) {
      this.crepe.editor.action((ctx) => {
        const view = ctx.get(editorViewCtx);
        deleteSelection(view.state, view.dispatch);
      });
    }
  }

  ngAfterViewChecked(): void {
    this.resolvePrivateImages();
    this.renderDynamicWidgets();
  }

  /**
   * Returns the rendered HTML of the markdown editor for printing/export purposes.
   */
  public getRenderedHtml(): string {
    return this.editorRoot?.nativeElement?.innerHTML || '';
  }

  /**
   * Finds all code blocks with language "widget" and renders the target Angular component
   * inside them dynamically.
   */
  private renderDynamicWidgets(): void {
    if (!this.editorRoot?.nativeElement) return;

    // In Crepe, code blocks are rendered with CodeMirror (.cm-content).
    // In standard milkdown they might be `code` tags.
    const textContainers =
      this.editorRoot.nativeElement.querySelectorAll('.cm-content, code');

    textContainers.forEach((container: Element) => {
      const htmlContainer = container as HTMLElement;
      const text = htmlContainer.textContent || '';

      // Remove zero-width spaces that CodeMirror might insert
      const cleanText = text.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();

      // Fast check if this is likely a widget configuration
      if (
        !cleanText.includes('"componentClassName"') ||
        !cleanText.startsWith('{')
      )
        return;

      try {
        const config = JSON.parse(cleanText);
        const manifest = WIDGET_REGISTRY[config.componentClassName];
        if (!manifest) return;

        // Find the outermost block container inside the editor (child of .ProseMirror)
        let blockContainer = container;
        while (
          blockContainer.parentElement &&
          !blockContainer.parentElement.classList.contains('ProseMirror') &&
          blockContainer.parentElement !== this.editorRoot.nativeElement
        ) {
          blockContainer = blockContainer.parentElement;
        }

        // If already rendered in this container, update its inputs instead of skipping
        if (this.renderedWidgets.has(blockContainer)) {
          const existingRef = this.renderedWidgets.get(blockContainer);

          // CRITICAL CHECK: ProseMirror sometimes updates the DOM and wipes out our custom appended elements.
          // We need to check if our wrapper is actually still in the DOM!
          const wrapperStillExists = blockContainer.querySelector(
            '.rapider-widget-render-container',
          );

          if (wrapperStillExists) {
            if (existingRef && config.inputs) {
              Object.keys(config.inputs).forEach((key) => {
                existingRef.setInput(key, config.inputs[key]);
              });
              existingRef.changeDetectorRef.detectChanges();
            }
            // Ensure native children remain hidden in case CodeMirror was re-rendered
            Array.from(blockContainer.children).forEach((child: any) => {
              if (
                !child.classList.contains('rapider-widget-render-container')
              ) {
                child.style.display = 'none';
              }
            });
            return;
          } else {
            // The wrapper was wiped by ProseMirror! We need to clean up the old ref and re-render.
            if (existingRef) {
              existingRef.destroy();
            }
            this.renderedWidgets.delete(blockContainer);
          }
        }

        // Mark as rendered BEFORE async loading to avoid race conditions!
        // We will store null temporarily, then replace with ComponentRef
        this.renderedWidgets.set(blockContainer, null as any);

        // Visually hide all native children (e.g. CodeMirror instance, language pickers, line numbers)
        // We do NOT hide the blockContainer itself so that Crepe's block drag handle can still hover it!
        Array.from(blockContainer.children).forEach((child: any) => {
          if (!child.classList.contains('rapider-widget-render-container')) {
            child.style.display = 'none';
          }
        });

        // Strip Crepe's default code-block visual styling (padding, borders, bg)
        // so our Angular component can occupy the full bounds natively
        (blockContainer as HTMLElement).style.cssText +=
          'padding: 0 !important; margin: 0 !important; border: none !important; background: transparent !important; box-shadow: none !important;';

        const wrapper = document.createElement('div');
        wrapper.className =
          'rapider-widget-render-container w-full relative group cursor-pointer my-2';

        wrapper.addEventListener('dblclick', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.zone.run(() => {
            this.crepe?.editor?.action((ctx) => {
              const view = ctx.get(editorViewCtx);
              try {
                const pos = view.posAtDOM(blockContainer, 0);
                const $pos = view.state.doc.resolve(pos);
                let targetNode = null;
                let targetPos = -1;
                for (let d = $pos.depth; d >= 0; d--) {
                  const node = $pos.node(d);
                  if (node.type.name === 'code_block') {
                    targetNode = node;
                    targetPos = $pos.before(d);
                    break;
                  }
                }
                if (targetNode && targetPos !== -1) {
                  this.selectedWidgetNode = targetNode;
                  this.selectedWidgetPos = targetPos;
                  this.selectedWidgetConfig = JSON.parse(
                    targetNode.textContent,
                  );
                  this.selectedWidgetManifest =
                    WIDGET_REGISTRY[
                      this.selectedWidgetConfig.componentClassName
                    ];
                  this.isCreatingNewWidget = false;
                  this.pendingWidgetFormData = {};
                  this.isWidgetModalVisible = true;
                }
              } catch (err) {
                console.error('Failed to locate node on dblclick', err);
              }
            });
          });
        });

        blockContainer.appendChild(wrapper);

        // If loader exists, render the actual angular component
        if (manifest.loader) {
          manifest.loader().then((componentClass) => {
            const componentRef = this.vcr.createComponent(componentClass);

            if (config.inputs) {
              Object.keys(config.inputs).forEach((key) => {
                componentRef.setInput(key, config.inputs[key]);
              });
            }

            wrapper.appendChild(componentRef.location.nativeElement);
            this.renderedWidgets.set(blockContainer, componentRef);
            // Trigger an initial change detection after attaching to ensure signals/inputs are processed
            componentRef.changeDetectorRef.detectChanges();
          });
        } else {
          // Fallback
          wrapper.innerHTML = `<div class="p-4 bg-component-inner border border-secondary-border rounded-lg text-sm text-primary">[${manifest.title}] Widget Render Placeholder</div>`;
        }
      } catch (e) {
        // Invalid JSON or manifest not found, just leave it as a raw code block
        console.error('Failed to render widget:', e, 'Text was:', cleanText);
      }
    });
  }

  /**
   * @method resolvePrivateImages
   * @description Scans the editor DOM for any images containing the private file placeholder path.
   * Fetches short-lived pre-signed URLs from the backend and natively swaps the `src` attribute
   * so the image displays correctly in the editor without modifying the underlying Markdown source state.
   */
  private resolvePrivateImages(): void {
    if (!this.editorRoot?.nativeElement) return;

    const images = this.editorRoot.nativeElement.querySelectorAll('img');

    images.forEach((img: HTMLImageElement) => {
      const srcAttr = img.getAttribute('src');

      // Target our specific private file path protocol
      if (srcAttr && srcAttr.startsWith('/rapider-private-file/')) {
        // If we already resolved it, ensure the DOM still has the real URL
        // (Milkdown sometimes re-renders and resets the attribute back to the markdown source)
        if (this.resolvedImages.has(srcAttr)) {
          const resolvedUrl = this.resolvedImages.get(srcAttr);
          if (resolvedUrl && img.src !== resolvedUrl) {
            img.src = resolvedUrl;
          }
          return;
        }

        // Prevent infinite loops / redundant network requests
        if (this.resolvingImages.has(srcAttr)) return;
        this.resolvingImages.add(srcAttr);

        // Give a visual indicator that it's loading securely
        img.style.opacity = '0.5';
        img.style.transition = 'opacity 0.3s ease';

        const fileId = srcAttr.replace('/rapider-private-file/', '');

        this.privateFileUploadService
          .getDownloadUrl(fileId)
          .then((url) => {
            this.resolvedImages.set(srcAttr, url);
            img.src = url;
            img.style.opacity = '1';
          })
          .catch((err) => {
            console.error(
              '[Milkdown] Failed to resolve private image URL:',
              err,
            );
            img.alt = 'Failed to load private image (Unauthorized or Expired)';
            img.style.opacity = '1';
          });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.crepe) {
      this.crepe.destroy();
    }
    document.removeEventListener('click', this.handleBlockMenuClick);

    if (this.renderPollingInterval) {
      clearInterval(this.renderPollingInterval);
    }

    this.renderedWidgets.forEach((compRef) => {
      if (compRef) compRef.destroy();
    });
    this.renderedWidgets.clear();
  }
}
